import { paths as CustomerCommunicationPaths } from '@luxuryescapes/contract-customer-communication';

import { request } from '~/services/common';

import { makeCustomerCommunicationV1URL } from './customerCommunicationRequestUtils';

type TodaysEscapesScheduleGetContract =
  CustomerCommunicationPaths['/api/customer-communication/v1/todays-escapes-schedule/schedules']['get'];

export type TodaysEscapesSchedule =
  TodaysEscapesScheduleGetContract['responses']['200']['content']['application/json']['result'];

export interface HeroPlannerScheduleDebugIdentifier {
  countryGroup: string;
  country: string;
  state: string;
  city: string;
  segment: string;
  loyalty: string;
}

interface HeroPlannerScheduleDebugOutcomeBase {
  /** The exact URL that was called, so it can be copied into Postman/curl when digging further */
  url: string;
  /** HTTP status; 0 when the request never got a response (network failure, CORS, etc) */
  status: number;
  durationMs: number;
}

export type HeroPlannerScheduleDebugOutcome =
  | (HeroPlannerScheduleDebugOutcomeBase & { ok: true; schedule: TodaysEscapesSchedule })
  | (HeroPlannerScheduleDebugOutcomeBase & { ok: false; message: string; body?: unknown });

export type HeroPlannerScheduleDebugFailure = Extract<HeroPlannerScheduleDebugOutcome, { ok: false }>;

// explicit guard because the `ok` discriminant doesn't narrow without strictNullChecks
export function isHeroPlannerScheduleDebugFailure(
  outcome: HeroPlannerScheduleDebugOutcome | undefined,
): outcome is HeroPlannerScheduleDebugFailure {
  return !!outcome && !outcome.ok;
}

async function readJsonBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

function getErrorMessage(body: unknown, fallback: string): string {
  if (body && typeof body === 'object' && 'message' in body && typeof body.message === 'string' && body.message) {
    return body.message;
  }
  return fallback;
}

/**
 * Fetches the schedule a subscriber with the given identifier would receive on `sendDate` - the same lookup the
 * email/push send performs. Unlike other request helpers this never throws for an HTTP error: a failing segment is
 * the result the debugger wants to show, and non-2xx responses are returned as data so they don't spam Sentry.
 */
async function requestGetTodaysEscapesScheduleDebug(
  brand: string,
  /**
   * YYYY-MM-DD
   */
  sendDate: string,
  identifier: HeroPlannerScheduleDebugIdentifier,
  signal?: AbortSignal,
): Promise<HeroPlannerScheduleDebugOutcome> {
  if (!brand) {
    throw 'missing brand';
  }
  if (!sendDate) {
    throw 'missing sendDate';
  }

  const url = makeCustomerCommunicationV1URL('/todays-escapes-schedule/schedules');
  url.searchParams.set('countryGroup', identifier.countryGroup);
  url.searchParams.set('country', identifier.country);
  url.searchParams.set('state', identifier.state);
  url.searchParams.set('city', identifier.city);
  url.searchParams.set('segment', identifier.segment);
  url.searchParams.set('loyalty', identifier.loyalty);
  url.searchParams.set('brand', brand);
  url.searchParams.set('sendDate', sendDate);

  const urlString = url.toString();
  const startedAt = performance.now();
  const elapsed = () => Math.round(performance.now() - startedAt);

  try {
    const response: Response = await request(urlString, {
      method: 'GET',
      credentials: 'include',
      returnResponse: true,
      allowNonOkResponse: true,
      signal,
    });
    const body = await readJsonBody(response);

    if (response.ok) {
      const result = (body as TodaysEscapesScheduleGetContract['responses']['200']['content']['application/json'])
        ?.result;
      return { ok: true, url: urlString, status: response.status, durationMs: elapsed(), schedule: result };
    }

    return {
      ok: false,
      url: urlString,
      status: response.status,
      durationMs: elapsed(),
      message: getErrorMessage(body, response.statusText || `Request failed with status ${response.status}`),
      body,
    };
  } catch (error) {
    // let react-query see cancellations so it can discard the result
    if (signal?.aborted) {
      throw error;
    }

    return {
      ok: false,
      url: urlString,
      status: 0,
      durationMs: elapsed(),
      message: error instanceof Error ? error.message : String(error),
    };
  }
}

export default requestGetTodaysEscapesScheduleDebug;
